/*  MUI  */
import {
    Box,
    Button,
    FormControlLabel,
    TableCell,
    Typography
} from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { DateFormat } from 'application/Shared/Components/Core';
import { RunReportLink } from 'application/Shared/Components/Reports/RunReportLink';
import { AuvikSwitch } from 'application/Shared/Components/Switches';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Route } from 'application/Shared/Helpers';

/*  Objects  */
import { Application } from 'objects/Application';
import {
    Breach,
    BreachClientsImpacted,
    BreachDescription,
    BreachResources,
    BreachScore
} from 'objects/Breach';

/*  React  */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const headers = (t) => [
    {
        field: 'dateAnnounced',
        renderHeader: t('Common.date'),
        width: '10%'
    },
    {
        field: 'description',
        renderHeader: t('Common.app_and_description'),
        width: '40%'
    },
    {
        field: 'severityScore',
        renderHeader: t('Common.score'),
        width: '5%'
    },
    {
        field: 'type',
        renderHeader: t('Common.sources'),
        width: '15%'
    },
    {
        renderHeader: t('Common.impact'),
        width: '15%'
    },
    {
        align: 'right',
        renderHeader: t('Common.actions'),
        width: '15%'
    }
];

const TableRow = ({ row }) => {
    const { t } = useTranslation();
    const { id } = row;
    const { partnerId } = useParams();
    const basePartnerRoute = Route.buildBasePartnerRoute({
        partnerId
    });

    return (
        <Breach data={row} key={id}>
            <TableCell>
                <DateFormat date={row.dateAnnounced} noTimeString />
            </TableCell>
            <TableCell>
                <Application
                    data={row.application}
                    nameLinkTo={`${basePartnerRoute}/applicationSettings`}
                >
                    <ApplicationNameLinkCell withCategory />
                </Application>
                <BreachDescription noTruncate includeSteps />
            </TableCell>
            <TableCell>
                <BreachScore />
            </TableCell>
            <TableCell>
                <BreachResources noTitle />
            </TableCell>
            <TableCell>
                <BreachClientsImpacted />
            </TableCell>
            <TableCell align="right">
                <RunReportLink
                    data-trackid="reports_runReportLink_routeTo_partnerVendorIncident"
                    reportParams={{
                        breachSelection: [id],
                        type: 'partnerBreachImpact'
                    }}
                >
                    <Button
                        data-trackid="partner_vendorIncidents_runReport"
                        variant="contained"
                        color="primary"
                    >
                        {t('Reports.run_report')}
                    </Button>
                </RunReportLink>
            </TableCell>
        </Breach>
    );
};

const searchFields = ['description'];

export const PartnerVendorIncidentsTable = () => {
    const { t } = useTranslation();
    const [onlyShowImpactedClients, setOnlyShowImpactedClients] =
        useState(true);

    const parameters = useMemo(
        () => ({
            includeApplication: true,
            includeClientsImpacted: true,
            includeOnlyImpactedClients: onlyShowImpactedClients
        }),
        [onlyShowImpactedClients]
    );

    const tableHeaders = useMemo(() => headers(t), [t]);

    return (
        <>
            <Box mb="8px" mt="8px">
                <FormControlLabel
                    control={
                        <Box>
                            <AuvikSwitch
                                checked={onlyShowImpactedClients}
                                onChange={() =>
                                    setOnlyShowImpactedClients(
                                        !onlyShowImpactedClients
                                    )
                                }
                                inputProps={{
                                    'aria-label': 'controlled'
                                }}
                            />
                        </Box>
                    }
                    label={
                        <Typography variant="p" style={{ marginLeft: '8px' }}>
                            {t('VendorIncident.onlyImpactedClients')}
                        </Typography>
                    }
                    style={{ marginLeft: 0 }}
                />
            </Box>
            <ManagedTable
                emptyRowsMessage={t('VendorIncident.noImpactedClients')}
                headers={tableHeaders}
                parameters={parameters}
                renderBody={(row) => <TableRow row={row} />}
                renderBodyKey="id"
                renderFooter
                rowsApi="/breach"
                rowsApiPreload
                rowsPerPage={5}
                searchEnabled
                searchFields={searchFields}
                sortDirection="DESC"
                sortFieldType="number"
                sortKey="dateAnnounced"
            />
        </>
    );
};
